import { getWellListFromTextArea } from "./getWellListFromTextArea";

function getStickListFromTextArea(stickList: Maybe<string>): string[] {
  return (stickList ?? "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function dedupe(items: string[]): string[] {
  return Array.from(new Set(items));
}

export type ResolveStickJobEntitiesResult =
  | { ok: true; wellList: string[]; stickList: string[] }
  | { ok: false; error: string };

// EVA-146: a stick calculation job can be all sticks, all wells, or a mix of both, so wells
// and sticks are validated together rather than independently requiring each to be non-empty.
export async function resolveStickJobEntities(
  wellListText: Maybe<string>,
  stickListText: Maybe<string>,
  validateWells: (wells: string[]) => Promise<string[]>,
  validateSticks: (sticks: string[]) => Promise<string[]>
): Promise<ResolveStickJobEntitiesResult> {
  const wellList = dedupe(getWellListFromTextArea(wellListText));
  const stickList = dedupe(getStickListFromTextArea(stickListText));

  if (wellList.length + stickList.length === 0) {
    return { ok: false, error: "At least one well or stick is required." };
  }

  if (wellList.length > 0) {
    let validWells: string[];
    try {
      validWells = await validateWells(wellList);
    } catch {
      return { ok: false, error: "Could not validate wells. Please try again." };
    }
    if (!Array.isArray(validWells)) {
      return { ok: false, error: "Could not validate wells. Please try again." };
    }
    if (validWells.length < wellList.length) {
      const missing = wellList.filter((well) => !validWells.includes(well));
      return { ok: false, error: `Invalid well(s): ${missing.join(", ")}` };
    }
  }

  if (stickList.length > 0) {
    let validSticks: string[];
    try {
      validSticks = await validateSticks(stickList);
    } catch {
      return { ok: false, error: "Could not validate sticks. Please try again." };
    }
    if (!Array.isArray(validSticks)) {
      return { ok: false, error: "Could not validate sticks. Please try again." };
    }
    const validSet = new Set(validSticks);
    const missing = stickList.filter((stick) => !validSet.has(stick));
    if (missing.length > 0) {
      return { ok: false, error: `Invalid stick(s): ${missing.join(", ")}` };
    }
  }

  return { ok: true, wellList, stickList };
}
